import { ErrorHandler, Injectable } from '@angular/core';
import {
  BleClient,
  BleService,
  ConnectionPriority,
  numbersToDataView,
  ScanResult,
} from '@capacitor-community/bluetooth-le';
import { first } from 'rxjs/operators';
import { BehaviorSubject, Subject } from 'rxjs';
import { Platform } from '@ionic/angular';
import { ViewportScroller } from '@angular/common';
import { Router } from '@angular/router';
import { DialogComponent, PopoverService, ErrorSinkService } from 'ngx-zcs-components';
import { wait } from 'ngx-core-services';
import { CommunicationChannel, decodeMultiQuery, encodeMultiQuery, ModbusLink, BLE_RETRY_POLICY } from 'ngx-modbus';

import { resolveInverterVersion } from './utils';

import { AccessSupportService } from './access-support.service';

import { DialogConfig } from '../shared/error-dialog.utils';
import { BlockTranslationsService } from '../block-translations.service';

const BLE_DEBUG = { ENABLED: true };
const bleLog = (msg: string, data?: unknown) => BLE_DEBUG.ENABLED && console.log(`[Modbus:BLE] ${msg}`, data ?? '');

const STOP_SCAN_AFTER_MILLIS = 5000;
const BLE_TIMEOUT = 2 * 1000;

enum BleErrorCode {
  BLUETOOTH_DISABLED = 'BLUETOOTH_DISABLED',
  LOCATION_DISABLED = 'LOCATION_DISABLED',
  PERMISSION_DENIED = 'Permission denied.',
  UNKNOWN = 'UNKNOWN',
}

interface BleError {
  message?: string;
  code?: string;
}

const getBluetoothErrorDialog = (error: BleError, labels: BlockTranslationsService['labels']): DialogConfig => {
  const code = error?.message || error?.code || BleErrorCode.UNKNOWN;

  switch(code) {
    case BleErrorCode.BLUETOOTH_DISABLED:
      return {
        icon: { type: 'ionic', name: 'bluetooth', color: 'danger' },
        name: labels.bluetoothEnableRequired,
        description: labels.bluetoothRetryAfterEnable,
        primary: { name: labels.bluetoothOpenSettings, action: 'openSettings' },
        secondary: { name: labels.commonCancel, action: 'dismiss' },
      };

    case BleErrorCode.LOCATION_DISABLED:
      return {
        icon: { type: 'ionic', name: 'location', color: 'warning' },
        name: labels.bluetoothLocationRequired,
        description: labels.bluetoothEnableLocationForBluetooth,
        primary: { name: labels.bluetoothOpenSettings, action: 'openLocationSettings' },
        secondary: { name: labels.commonCancel, action: 'dismiss' },
      };

    case BleErrorCode.PERMISSION_DENIED:
      return {
        icon: { type: 'ionic', name: 'settings', color: 'warning' },
        name: labels.bluetoothLocationRequired,
        description: labels.bluetoothEnableLocationForBluetooth,
        primary: { name: labels.bluetoothOpenSettings, action: 'openSettings' },
        secondary: { name: labels.commonCancel, action: 'dismiss' },
      };

    default:
      return {
        icon: { type: 'ionic', name: 'alert-circle', color: 'danger' },
        name: labels.commonError,
        description: labels.bluetoothUnableToScan,
        primary: { name: labels.commonRetry, action: 'retry' },
        secondary: { name: labels.commonCancel, action: 'dismiss' },
      };
  }
};

const openDeviceSettings = async (): Promise<void> => {
  try {
    await BleClient.openAppSettings();
  } catch (error: unknown) {
    bleLog('Failed to open device settings:', error);
  }
};

const openLocationSettings = async (): Promise<void> => {
  try {
    await BleClient.openLocationSettings();
  } catch (error: unknown) {
    bleLog('Failed to open location settings:', error);
  }
};

const handleErrorAction = async (action: string, onRetry: () => Promise<void>): Promise<void> => {
  switch(action) {
    case 'retry':
      await onRetry();
      break;

    case 'openSettings':
      await openDeviceSettings();
      break;

    case 'openLocationSettings':
      await openLocationSettings();
      break;

    case 'dismiss':
    default:
      break;
  }
};

@Injectable({
  providedIn: 'root',
})
export class BluetoothLeService implements CommunicationChannel {
  readonly connectionStatus$ = new BehaviorSubject<boolean>(false);
  bluetoothConnectedDevice?: ScanResult;
  bluetoothIsScanning = false;
  bluetoothScanResults: ScanResult[] = [];
  services: BleService[] = [];
  command?: ModbusLink;
  refreshByDefault = 20 * 1000;
  MTU = 240;

  concurrentQuery = {
    count: 1,
    encodeMultiQuery,
    decodeMultiQuery,
  };

  readonly response$ = new Subject<Uint8Array>();

  get deviceId(): string | undefined {
    return this.bluetoothConnectedDevice?.device.name;
  }

  loggerId?: number;

  get inverterId(): string | undefined {
    return this.bluetoothConnectedDevice?.device.name;
  }

  get isConnected(): boolean {
    const isConnected = !!this.bluetoothConnectedDevice;
    this.connectionStatus$.next(isConnected);
    return isConnected;
  }

  constructor(
    private readonly accessSupportService: AccessSupportService,
    private readonly blockTranslationsService: BlockTranslationsService,
    private readonly customErrorHandlerService: ErrorHandler,
    private readonly errorSink: ErrorSinkService,
    private readonly platform: Platform,
    private readonly popoverService: PopoverService,
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller,
  ) {
    this.interaction = this.interaction.bind(this);
    this.scan = this.scan.bind(this);
    this.close = this.close.bind(this);
  }

  async requestConnectionPriority(priority: 'high' | 'low' | 'balanced'): Promise<void> {
    if (!this.bluetoothConnectedDevice) {
      return;
    }
    const device = this.bluetoothConnectedDevice?.device;
    await BleClient.requestConnectionPriority(
      device.deviceId,
      priority === 'high'
        ? ConnectionPriority.CONNECTION_PRIORITY_HIGH
        : priority === 'low'
          ? ConnectionPriority.CONNECTION_PRIORITY_LOW_POWER
          : ConnectionPriority.CONNECTION_PRIORITY_BALANCED,
    );
  }

  private async handleError(error: BleError, onRetry: () => Promise<void>): Promise<void> {
    const dialogConfig = getBluetoothErrorDialog(error, this.blockTranslationsService.labels);

    const dialog = await this.popoverService.simple({
      component: DialogComponent,
      backdropDismiss: false,
      componentProps: {
        icon: dialogConfig.icon,
        name: dialogConfig.name,
        description: dialogConfig.description,
        primary: {
          name: dialogConfig.primary.name,
          click: async () => {
            dialog.dismiss();
            await handleErrorAction(dialogConfig.primary.action, onRetry);
          },
        },
        secondary: dialogConfig.secondary ? {
          name: dialogConfig.secondary.name,
          click: () => dialog.dismiss(),
        } : undefined,
      },
    });
  }

  async scan(): Promise<void> {
    try {
      await BleClient.initialize();

      const isBleEnabled = await BleClient.isEnabled().catch((_) => false);
      if (!isBleEnabled) {
        try {
          await BleClient.enable();
        } catch  {
          throw new Error(BleErrorCode.BLUETOOTH_DISABLED);
        }
      }

      const isGeoEnabled = await BleClient.isLocationEnabled().catch((_) => false);
      if (!isGeoEnabled && this.platform.is('android')) {
        throw new Error(BleErrorCode.LOCATION_DISABLED);
      }

      this.bluetoothScanResults = [];
      this.bluetoothIsScanning = true;

      await this.close();

      await BleClient.requestLEScan(
        { allowDuplicates: false },
        this.onBluetoothDeviceFound.bind(this),
      );

      await wait(STOP_SCAN_AFTER_MILLIS);
    } catch (error: unknown) {
      bleLog('Error during BLE scan:', error);
      await this.handleError(error as BleError, () => this.scan());
    } finally {
      this.bluetoothIsScanning = false;
      await BleClient.stopLEScan();
    }
  }

  async open(scanResult: ScanResult) {
    await this.close();
    const device = scanResult.device;

    const connectionRetryCount = 3;
    let connectionRetry = 0;

    while (connectionRetry < connectionRetryCount) {
      try {
        await BleClient.connect(
          device.deviceId,
          this.onBluetoothDeviceDisconnected.bind(this),
        );
        break;
      } catch (error: unknown) {
        bleLog(`Error during BLE connection: ${JSON.stringify(error)}`);
        connectionRetry++;
      }
    }

    await this.requestConnectionPriority('high');
    BleClient.disableQueue();

    this.connectionStatus$.next(true);
    this.bluetoothConnectedDevice = scanResult;
    const deviceName = device.name ?? device.deviceId;

    const handleFatalError = async (error?: unknown) => {
      this.errorSink.reportTransport(
        'Connection lost',
        `Fatal BLE error for device ${deviceName}`,
      );
      await this.close();
      this.router.navigate(['/']);
    };

    this.command?.destroy();

    const inverterVersion = resolveInverterVersion(deviceName);

    this.command = new ModbusLink({
      deviceId: this.bluetoothConnectedDevice?.device.deviceId || deviceName,
      inverterVersion,
      executeCmd: this.interaction,
      handleFatalError,
      maxTimeoutInMs: BLE_TIMEOUT,
      concurrentQuery: this.concurrentQuery,
      modbusAddress: 1,
      retryPolicy: BLE_RETRY_POLICY,
    });
    bleLog(`CONNECTED ${deviceName}, inverterVersion=${inverterVersion ?? 'legacy'}`);

    this.services = await BleClient.getServices(device.deviceId);
    await this.attachToNotifyServices();
  }

  async attachToNotifyServices() {
    if (!this.bluetoothConnectedDevice) {
      return;
    }
    const device = this.bluetoothConnectedDevice?.device;
    for (const service of this.services) {
      for (const characteristic of service.characteristics) {
        if (characteristic.properties.notify) {
          await BleClient.startNotifications(
            device.deviceId,
            service.uuid,
            characteristic.uuid,
            this.onBluetoothNotify.bind(this),
          );
        }
      }
    }
  }

  async close() {
    const connectedDevices = await BleClient.getConnectedDevices([]);
    for (const device of connectedDevices) {
      await BleClient.disconnect(device.deviceId);
    }
    this.bluetoothConnectedDevice = undefined;
    this.command = undefined;
    this.connectionStatus$.next(false);

    this.bluetoothScanResults = [];
  }

  async interaction(cmd: Uint8Array): Promise<Uint8Array> {
    if (!this.bluetoothConnectedDevice) {
      throw new Error('No bluetooth device connected');
    }

    const toCmdSend = Array.from(cmd);
    const toCmdSendAsString = toCmdSend.map((x) => x.toString(16).padStart(2, '0')).join('');

    const segmentSize = this.MTU;
    let currentPosition = 0;

    while (currentPosition < cmd.length) {
      const segmentLength = Math.min(segmentSize, cmd.length - currentPosition);
      const segment = cmd.slice(currentPosition, currentPosition + segmentLength);
      currentPosition += segmentLength;

      const toSend = Array.from(segment);
      const toSendAsString = toSend.map((x) => x.toString(16).padStart(2, '0')).join('');

      const services = this.services;
      for (const service of services) {
        for (const characteristic of service.characteristics) {
          if (characteristic.properties.writeWithoutResponse) {
            try {
              const sendTime = new Date().toISOString();
              bleLog(`${sendTime} SEND - 0x${toSendAsString}`);
              await BleClient.writeWithoutResponse(
                this.bluetoothConnectedDevice.device.deviceId,
                service.uuid,
                characteristic.uuid,
                numbersToDataView(toSend),
                {
                  timeout: BLE_TIMEOUT,
                },
              );
            } catch (error: unknown) {
              bleLog(`ERROR sending segment: ${JSON.stringify(error)}`);
            }
          }
        }
      }
    }

    const response = await this.response$.pipe(first()).toPromise() || [];
    const responseAsString = Array.from(response).map((x) => x.toString(16).padStart(2, '0')).join('');

    const receivedTime = new Date();
    bleLog(`${receivedTime.toISOString()} RECEIVED - 0x${responseAsString}`);
    return response;
  }

  async onBluetoothDeviceFound(result: ScanResult) {
    const device = result.device;
    const deviceName = device.name || '';

    const isSupported = deviceName.trim() && (await this.accessSupportService.isSupported(deviceName));
    if (!isSupported) {
      return;
    }
    bleLog('FOUND', result);
    this.bluetoothScanResults.push(result);
  }

  onBluetoothNotify(result: DataView) {
    this.response$.next(new Uint8Array(result.buffer));
  }

  onBluetoothDeviceDisconnected(disconnectedDeviceId: string) {
    bleLog(`DISCONNECTED ${disconnectedDeviceId}`);
    this.bluetoothConnectedDevice = undefined;
    this.command = undefined;
    this.connectionStatus$.next(false);
    this.router.navigate(['/']).then(() => {
      this.viewportScroller.scrollToPosition([0, 0]);
    });
  }
}
